import { useEffect, useState } from 'react'
import { usePlaidLink } from 'react-plaid-link'
import { useNavigate } from 'react-router-dom'
import { customerData, PlaidAuthorization } from '../../utilities/CustomerData'
import { apiURL } from '../../utilities/URLs'
import { refreshAccessToken } from '../../utilities/extensions'
import { PlaidAuthorizationModal } from '../Payment/PlaidAuthorizationModal'

export function DashboardResetAgreement() {
  const navigate = useNavigate()
  const [plaidLinkToken, setPlaidLinkToken] = useState('')
  const [plaidPublicToken, setPlaidPublicToken] = useState('')
  const { open, ready } = usePlaidLink({
    token: plaidLinkToken,
    onSuccess: (public_token, metadata) => {
      setPlaidPublicToken(public_token)
    },
    onExit: async (error, metadata) => {
      if (error !== null)
        await handlePlaidLinkError(error)
    }
  })

  const [fetchingBankInfo, setFetchingBankInfo] = useState(false)
  const [plaidAuthorization, setPlaidAuthorization] = useState(new PlaidAuthorization())
  const [linkedPlaid, setLinkedPlaid] = useState(false)

  /**
   * @param {import('react-plaid-link').PlaidLinkError} error 
   */
  const handlePlaidLinkError = async (error) => {
    const form = new FormData()
    form.append('plaidLinkError', JSON.stringify({
      customerId: customerData.customerLeadData.leadContact.contactId,
      customerName: customerData.customerLeadData.leadContact.contactName,
      customerEmail: customerData.customerLeadData.leadContact.contactEmail,
      plaidLinkError: error
    }))

    const plaidLinkErrorResponse = await fetch(`${apiURL}/api/uploadPlaidLinkError`, {
      method: 'POST',
      headers: {
        'Authorization': customerData.accessToken
      },
      body: form
    })
    const plaidLinkErrorData = await plaidLinkErrorResponse.json()
    if (plaidLinkErrorData.response.error) {
      if (plaidLinkErrorData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handlePlaidLinkError(error) : navigate('/', { state: { expired: true } })
      } else
        console.error(plaidLinkErrorData.response.error)
    }
  }

  useEffect(() => {
    const handleExchangePublicToken = async () => {
      setFetchingBankInfo(true)

      const exchangePublicTokenResponse = await fetch(`${apiURL}/api/exchangePublicToken?publicToken=${encodeURIComponent(plaidPublicToken)}&customerId=${encodeURIComponent(customerData.customerLeadData.leadContact.contactId)}&customerName=${encodeURIComponent(customerData.customerLeadData.leadContact.contactName)}&paymentAmount=${encodeURIComponent(customerData.customerInvoiceData.invoiceRemainingBalance)}`, {
        headers: {
          'Authorization': customerData.accessToken
        }
      })
      const exchangePublicTokenData = await exchangePublicTokenResponse.json()
      setFetchingBankInfo(false)
      if (exchangePublicTokenData.response.error) {
        if (exchangePublicTokenData.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          refreshedToken && refreshedToken.length > 0 ? await handleExchangePublicToken() : navigate('/', { state: { expired: true } })
        } else
          console.error(exchangePublicTokenData.response.error)
      } else
        setPlaidAuthorization(new PlaidAuthorization(exchangePublicTokenData.response))
    }

    if (plaidPublicToken.length > 0)
      handleExchangePublicToken()
  }, [plaidPublicToken])

  useEffect(() => {
    const handleGetLinkToken = async () => {
      const linkTokenResponse = await fetch(`${apiURL}/api/createPlaidLinkToken?customerId=${encodeURIComponent(customerData.customerLeadData.leadContact.contactId)}`, {
        headers: {
          'Authorization': customerData.accessToken
        }
      })
      const linkTokenData = await linkTokenResponse.json()
      if (linkTokenData.response.error) {
        if (linkTokenData.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          refreshedToken && refreshedToken.length > 0 ? await handleGetLinkToken() : navigate('/', { state: { expired: true } })
        } else
          console.error(linkTokenData.response.error)
      } else
        setPlaidLinkToken(linkTokenData.response.linkToken)
    }

    handleGetLinkToken()
  }, [])

  const handleAgreement = () => {
    setPlaidAuthorization(new PlaidAuthorization())
    setLinkedPlaid(true)
  }

  return (
    <div className='shadow-full rounded-xl p-4 lg:w-full w-11/12 h-fit mt-10 flex flex-col bg-[#bceda6] font-mont'>
      <div className='w-full max-sm:scale-90 flex flex-col items-center'>
        <p className='font-bold text-3xl text-center mt-4'>Change your ACH Connected Account</p>
        {!linkedPlaid ? (
          <div className='flex flex-col items-center gap-4 mt-10'>
            <p>Ready to change your ACH payment method? Click the button below to get started.</p>
            <button
              onClick={() => open()}
              disabled={!ready || fetchingBankInfo}
              className={`w-52 h-12 rounded-lg text-white ${ready && !fetchingBankInfo ? 'bg-blue-500 hover:bg-blue-600 transition duration-200' : 'bg-gray-400 cursor-not-allowed'}`}>
              Reconnect ACH
            </button>
            {fetchingBankInfo && (
              <div className='flex flex-row items-center justify-center gap-2'>
                <div className='loader ease-linear rounded-full border-[6px] border-t-[6px] h-8 w-8 mt-4 mb-4'></div>
                <p>Grabbing Banking Information...</p>
              </div>
            )}
          </div>
        ) : (
          <div className='flex flex-col items-center gap-4 mt-10'>
            <p>We have successfully updated your ACH payment!</p>
            <p>Please make sure to <span className='font-bold'>redownload</span> your documents for the updated 8821 form.</p>
          </div>
        )}
      </div>
      {plaidAuthorization.accessToken && (
        <PlaidAuthorizationModal
          _plaidAuthorization={plaidAuthorization}
          paymentAmount={customerData.customerInvoiceData.invoiceRemainingBalance}
          handleAgreement={handleAgreement}
        />
      )}
    </div>
  )
}